import * as React from 'react'

import getConfig from 'next/config'
import NextRouter from 'next/router'

import { CmsComponentSlot, CmsHeadTags, useBloomreach } from '@grandvisionhq/bloomreach'
import { NextPage } from '@grandvisionhq/next'
import {
  AlgoliaConfigProvider,
  AlgoliaConfigProviderProps,
  fetchAlgoliaConfig,
  SearchIndex,
  SearchPage as SearchPageComponent,
} from '@grandvisionhq/search'
import { useIntl } from '@grandvisionhq/state'
import { compose } from '@grandvisionhq/utils'

import { withCms, withLayout } from '../cms'
import { INTL_DEFAULTS } from '../configuration/intl'
import { createAlgoliaConfig } from '../util/algolia'

const { publicRuntimeConfig } = getConfig()

type InitialProps = {
  queryParams: Record<string, string | string[] | undefined>
  algoliaConfig: AlgoliaConfigProviderProps
}

type Props = InitialProps

const SearchPage: NextPage<Props, InitialProps> = ({ algoliaConfig, queryParams = {} }) => {
  const { getLabel } = useIntl()
  const { siteLogos } = useBloomreach()

  const pageTitle = queryParams.q
    ? `${getLabel('search.results.title')} '${queryParams.q}'`
    : getLabel('search.pageTitle')

  const cleanedQueryParams = Object.keys(queryParams).reduce((acc, key) => {
    const _acc = acc
    if (queryParams[key] !== undefined) _acc[key] = queryParams[key]
    return _acc
  }, {})

  return (
    <>
      <CmsHeadTags title={pageTitle} noIndex={true} noFollow={true} />
      <AlgoliaConfigProvider {...algoliaConfig} NextRouter={NextRouter}>
        <CmsComponentSlot path="main/top-container" />
        <SearchPageComponent
          pageTitle={pageTitle}
          reimbursableLogos={{
            default: siteLogos.reimbursable,
            alternative: siteLogos.reimbursableAlternative,
          }}
          algoliaConfig={publicRuntimeConfig.algolia}
          queryParams={cleanedQueryParams}
        />
        <CmsComponentSlot path="main/bottom-container" />
      </AlgoliaConfigProvider>
    </>
  )
}

SearchPage.getInitialProps = async ({ query, asPath = '' }) => {
  const contentIndex: SearchIndex = publicRuntimeConfig.algolia.indexes.find(
    ({ type }) => type === 'content'
  )

  const algoliaConfig = await fetchAlgoliaConfig({
    ...createAlgoliaConfig(asPath, undefined, undefined, {
      indices: [contentIndex.name],
    }),
    locale: INTL_DEFAULTS.locale,
    ruleContexts: ['search'],
  })

  return { queryParams: query, algoliaConfig }
}

export default compose<Props, Props>(withCms(), withLayout())(SearchPage)
